/**
 * Timeframes del dashboard (§10): cada uno es una VENTANA (cuánto pasado se ve)
 * y un tamaño de VELA (cuánto agrega cada punto del gráfico). Son lo mismo que
 * los «zoom» de un gráfico de trading: al alejar la vista, la vela engorda para
 * que el número de puntos se mantenga legible.
 *
 * La ventana siempre es MÓVIL y termina en el `to` de los filtros (no en el
 * año calendario), y su largo es múltiplo exacto del tamaño de vela, para que
 * ninguna vela quede partida y la misma vela de dos años distintos se compare
 * (YoY) por su índice dentro de la ventana.
 */

export type Timeframe = '2s' | '1m' | '3m' | '6m' | '1a' | '5a';

export interface TimeframeSpec {
  value: Timeframe;
  /** Texto del botón del selector. */
  label: string;
  /** Coletilla de los títulos de los gráficos. */
  desc: string;
  /** Grano de la vela: N días, un mes natural o un año natural. */
  grano: 'dia' | 'mes' | 'anio';
  /** Días por vela (solo `grano: 'dia'`). */
  velaDias: number;
  /** Largo de la ventana en días / meses / años, según el grano. */
  ventana: number;
}

export const TIMEFRAME_SPECS: readonly TimeframeSpec[] = [
  {
    value: '2s',
    label: '2 sem',
    desc: 'últimas 2 semanas · velas diarias',
    grano: 'dia',
    velaDias: 1,
    ventana: 14, // 14 velas
  },
  {
    value: '1m',
    label: '1 mes',
    desc: 'último mes · velas de 3 días',
    grano: 'dia',
    velaDias: 3,
    ventana: 30, // 10 velas
  },
  {
    value: '3m',
    label: '3 meses',
    desc: 'últimos 3 meses · velas semanales',
    grano: 'dia',
    velaDias: 7,
    ventana: 91, // 13 velas
  },
  {
    value: '6m',
    label: '6 meses',
    desc: 'últimos 6 meses · velas de 2 semanas',
    grano: 'dia',
    velaDias: 14,
    ventana: 182, // 13 velas
  },
  {
    value: '1a',
    label: '1 año',
    desc: 'últimos 12 meses · velas mensuales',
    grano: 'mes',
    velaDias: 0,
    ventana: 12, // 12 velas
  },
  {
    value: '5a',
    label: '5 años',
    desc: 'últimos 5 años · velas anuales',
    grano: 'anio',
    velaDias: 0,
    ventana: 5, // 5 velas
  },
] as const;

export const DEFAULT_TIMEFRAME: Timeframe = '1m';

const BY_VALUE = new Map(TIMEFRAME_SPECS.map((s) => [s.value, s]));

/**
 * Nombres viejos del parámetro `tf` (antes de que el timeframe llevara tamaño
 * de vela). Se siguen aceptando para no romper deep links ni vistas guardadas.
 */
const LEGACY: Record<string, Timeframe> = {
  semanal: '2s',
  mensual: '1m',
  anual: '1a',
  quinquenal: '5a',
};

export function isTimeframe(x: unknown): x is Timeframe {
  return typeof x === 'string' && BY_VALUE.has(x as Timeframe);
}

/** Lee el `tf` de la URL (acepta los nombres viejos); `null`/basura → defecto. */
export function parseTimeframe(raw: string | null | undefined): Timeframe {
  if (isTimeframe(raw)) return raw;
  return (raw && LEGACY[raw]) || DEFAULT_TIMEFRAME;
}

export function timeframeSpec(tf: Timeframe): TimeframeSpec {
  return BY_VALUE.get(tf) ?? BY_VALUE.get(DEFAULT_TIMEFRAME)!;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Desplaza una fecha 'YYYY-MM-DD' N días (UTC). */
export function shiftDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!) + n * 86400000);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/** Días entre dos fechas 'YYYY-MM-DD' (b - a). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86400000);
}

/** Desplaza una fecha 'YYYY-MM-DD' años enteros, recortando el día (29-feb → 28). */
export function shiftYearIso(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const ny = y! + delta;
  const diasDelMes = new Date(Date.UTC(ny, m!, 0)).getUTCDate();
  return `${ny}-${pad2(m!)}-${pad2(Math.min(d!, diasDelMes))}`;
}

/** Primer día del mes que está `n` meses antes del mes de `iso`. */
function monthStartBack(iso: string, n: number): string {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const dt = new Date(Date.UTC(y, m - 1 - n, 1));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-01`;
}

export interface Ventana {
  from: string;
  to: string;
}

/**
 * Ventana del gráfico y su equivalente del año previo (para el YoY), anclada
 * en `to`. El quinquenal no tiene previo: ya muestra 5 años en un solo gráfico.
 */
export function chartWindows(
  to: string,
  tf: Timeframe,
): { cur: Ventana; prev: Ventana; hasPrior: boolean } {
  const spec = timeframeSpec(tf);
  if (spec.grano === 'anio') {
    const anio = Number(to.slice(0, 4));
    const cur = { from: `${anio - (spec.ventana - 1)}-01-01`, to: `${anio}-12-31` };
    return { cur, prev: cur, hasPrior: false };
  }
  const from =
    spec.grano === 'mes'
      ? monthStartBack(to, spec.ventana - 1)
      : shiftDays(to, -(spec.ventana - 1));
  return {
    cur: { from, to },
    prev: { from: shiftYearIso(from, -1), to: shiftYearIso(to, -1) },
    hasPrior: true,
  };
}
